import React from "react";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import {
	PostElementParagraph,
	PostElementListItem,
	PostElementUl,
	PostElementTitleH2,
	PostElementLink,
	PostElementTitleH3,
} from "@insightfulscience/atomic-react/PostElement";
import GuidePageTemplate from "@insightfulscience/atomic-react/GuidePageTemplate";
import GuidePageTemplateMain from "@insightfulscience/atomic-react/GuidePageTemplate/Main";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "@src/SiteLayout";
import resources from "../locales/legal-disclaimers-page";

const LegalDisclaimers = () => {
	const [t] = useLocale("legal-disclaimers-page");

	const titleAnchor = {
		privacy_policy: "privacy_policy",
		how_to_buy: "how_to_buy",
		website_terms: "website_terms",
		property_rights: "property_rights",
		license_to_use: "license_to_use",
		disclaimer_of_limitation: "disclaimer_of_limitation",
		general_terms: "general_terms",
		notice_of_dispute: "notice_of_dispute",
		arbitration: "arbitration",
		class_action_waiver: "class_action_waiver",
		costs: "costs",
		resolution_of_disputes: "resolution_of_disputes",
		reCAPTCHA: "reCAPTCHA",
		location_data: "location_data",
		contact_details: "contact_details",
		entire_agreement: "entire_agreement",
	};

	return (
		<SiteLayout hero>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					isImageBaseUrl
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
			</PageMeta>
			<Hero>
				<HeroTitle>{t("TITLE")}</HeroTitle>
			</Hero>
			<GuidePageTemplate sidebar={false}>
				<GuidePageTemplateMain sidebar={false}>
					<PostElementTitleH2>{t("h2_1")}</PostElementTitleH2>
					<PostElementParagraph>{t("p_1")}</PostElementParagraph>
					<PostElementUl>
						<PostElementListItem>
							<PostElementLink href="legal/terms-of-service">
								Product Terms of Service
							</PostElementLink>
						</PostElementListItem>
						<PostElementListItem>
							<PostElementLink href={`#${titleAnchor.privacy_policy}`}>
								Privacy Policy
							</PostElementLink>
						</PostElementListItem>
						<PostElementListItem>
							<PostElementLink href={`#${titleAnchor.how_to_buy}`}>How to Buy</PostElementLink>
						</PostElementListItem>
						<PostElementListItem>
							<PostElementLink href={`#${titleAnchor.website_terms}`}>
								Website Terms
							</PostElementLink>
							<PostElementUl>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.property_rights}`}>
										Intellectual Property Rights
									</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.license_to_use}`}>
										License to Use Website
									</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.disclaimer_of_limitation}`}>
										Disclaimer of Limitation of Liability
									</PostElementLink>
								</PostElementListItem>
							</PostElementUl>
						</PostElementListItem>
						<PostElementListItem>
							<PostElementLink href={`#${titleAnchor.general_terms}`}>
								General Terms
							</PostElementLink>
							<PostElementUl>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.resolution_of_disputes}`}>
										Resolution of Disputes
									</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.notice_of_dispute}`}>
										Step 1. Notice of Dispute
									</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.arbitration}`}>
										Step 2: Arbitration
									</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.class_action_waiver}`}>
										Class Action Waiver
									</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.costs}`}>Costs</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.location_data}`}>
										Location Data
									</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.reCAPTCHA}`}>reCAPTCHA</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.contact_details}`}>
										Our Contact Details
									</PostElementLink>
								</PostElementListItem>
								<PostElementListItem>
									<PostElementLink href={`#${titleAnchor.entire_agreement}`}>
										Entire Agreement
									</PostElementLink>
								</PostElementListItem>
							</PostElementUl>
						</PostElementListItem>
					</PostElementUl>
					<PostElementTitleH2>{t("h2_2")}</PostElementTitleH2>
					<PostElementParagraph>
						For information about licenses governing your use of our Products, please see the{" "}
						<PostElementLink href="/legal/terms-of-service">
							SnapGene Terms of Service
						</PostElementLink>{" "}
						(the &quot;Terms of Service&quot;). By accessing or using the Service, you agree to be
						subject to the terms and conditions of the Terms of Service.
					</PostElementParagraph>
					<PostElementTitleH2 id={titleAnchor.privacy_policy}>{t("h2_3")}</PostElementTitleH2>
					<PostElementParagraph>
						For information about our data practices, please see our{" "}
						<PostElementLink href="https://www.dotmatics.com/privacy-policy" target="_blank">
							Privacy Policy
						</PostElementLink>
						. By accessing or using the Site, you (individually and on behalf of the organization or
						business entity whom you represent) agree that we can collect and use your information
						in accordance with the Privacy Policy. For clarity, the{" "}
						<PostElementLink href="https://www.dotmatics.com/privacy-policy" target="_blank">
							Privacy Policy
						</PostElementLink>{" "}
						also applies to your use of our products.
					</PostElementParagraph>
					<PostElementTitleH2 id={titleAnchor.how_to_buy}>{t("h2_4")}</PostElementTitleH2>
					<PostElementParagraph>
						A subscription allows registered members (and individuals authorized by the registered
						member to access the Service) (hereafter, &quot;Authorized User&quot; or
						&quot;Authorized Users&quot;) to access the Service. No matter the role, a subscription
						is required for each Authorized User. A subscription may be procured through the
						SnapGene interface (i.e. online checkout process), or in some cases, via an order form
						entered into between registered member and us (hereafter, the “Order Form”). Please see{" "}
						<PostElementLink href="/pricing">How to Buy</PostElementLink> for more information on
						procuring subscriptions and adding new Authorized Users.
					</PostElementParagraph>
					<PostElementTitleH2 id={titleAnchor.website_terms}>{t("h2_5")}</PostElementTitleH2>
					<PostElementParagraph>
						The following additional terms govern your use of the website. Please review them
						carefully.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.property_rights}>
						Intellectual Property Rights
					</PostElementTitleH3>
					<PostElementParagraph>
						Unless otherwise stated, we or our licensors own the intellectual property rights in the
						website and material on the website. Subject to the license below, all our intellectual
						property rights are reserved.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.license_to_use}>
						License to Use Website
					</PostElementTitleH3>
					<PostElementParagraph>
						You may view, download for caching purposes only, and print pages or documents from the
						website, provided that:(a) you must not republish material from this website (including
						republication on another website), or reproduce or store material from this website in
						any public or private electronic retrieval system;(b) you must not reproduce, duplicate,
						copy, sell, resell, visit, or otherwise exploit our website or material on our website
						for a commercial purpose, without our express written consent.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.disclaimer_of_limitation}>
						Disclaimer and Limitation of Liability
					</PostElementTitleH3>
					<PostElementParagraph>
						WHILE WE ENDEAVOR TO ENSURE THAT THE INFORMATION ON THIS WEBSITE IS CORRECT, WE DO NOT
						WARRANT ITS COMPLETENESS OR ACCURACY; NOR DO WE NOT COMMIT TO ENSURING THAT THE WEBSITE
						REMAINS AVAILABLE OR THAT THE MATERIAL ON THIS WEBSITE IS KEPT UP-TO-DATE.TO THE MAXIMUM
						EXTENT PERMITTED BY APPLICABLE LAW WE EXCLUDE ALL REPRESENTATIONS, WARRANTIES AND
						CONDITIONS (INCLUDING, WITHOUT LIMITATION, THE CONDITIONS IMPLIED BY LAW OF SATISFACTORY
						QUALITY, FITNESS FOR PURPOSE AND THE USE OF REASONABLE CARE AND SKILL).OUR LIABILITY IS
						LIMITED AND EXCLUDED TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW. WE WILL NOT
						BE LIABLE FOR ANY DIRECT, INDIRECT OR CONSEQUENTIAL LOSS OR DAMAGE ARISING UNDER THESE
						TERMS AND CONDITIONS OR IN CONNECTION WITH OUR WEBSITE, WHETHER ARISING IN TORT,
						CONTRACT, OR OTHERWISE – INCLUDING, WITHOUT LIMITATION, ANY LOSS OF PROFIT, CONTRACTS,
						BUSINESS, GOODWILL, DATA, INCOME, REVENUE OR ANTICIPATED SAVINGS.HOWEVER, NOTHING IN
						THESE TERMS AND CONDITIONS SHALL EXCLUDE OR LIMIT OUR LIABILITY FOR FRAUD, FOR DEATH OR
						PERSONAL INJURY CAUSED BY OUR NEGLIGENCE, OR FOR ANY OTHER LIABILITY WHICH CANNOT BE
						EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
					</PostElementParagraph>
					<PostElementTitleH2 id={titleAnchor.general_terms}>{t("h2_6")}</PostElementTitleH2>
					<PostElementTitleH3 id={titleAnchor.resolution_of_disputes}>
						RESOLUTION OF DISPUTES - MANDATORY ARBITRATION AND CLASS ACTION WAIVER
					</PostElementTitleH3>
					<PostElementParagraph>
						We offer the following process to help you resolve a complaint or Dispute (as defined
						below) that you may have with GSL BioTech or its Third Party Providers in connection
						with the use of this Service. Please read this section carefully. Our Customer Service
						Department, which you can reach at{" "}
						<PostElementLink href="https://support.snapgene.com/hc/en-us/" target="_blank">
							support.snapgene.com
						</PostElementLink>{" "}
						can help you resolve most of your concerns. If our Customer Service Department is unable
						to resolve your complaint or Dispute, you agree to take the following steps to resolve
						any Dispute you may have with us.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.notice_of_dispute}>{t("h3_6_1")}</PostElementTitleH3>
					<PostElementParagraph>
						You must first try to resolve any complaint or Dispute with us through our Notice of
						Dispute process. You begin by submitting a &quot;Notice of Dispute&quot; with any
						supporting documents or other information by U.S. Mail to:
					</PostElementParagraph>
					<PostElementParagraph>
						GSL Biotech, LLC <br />
						Attention: Notice of Dispute <br />
						225 Franklin Street. Fl. 26 <br />
						BOSTON, MA 02110
					</PostElementParagraph>
					<PostElementParagraph>
						A &quot;Notice of Dispute&quot; is a written form in which you provide your name,
						address, contact information, email address, the facts regarding your Dispute, and the
						relief you are requesting from us. Once we receive your Notice of Dispute, you and we
						will attempt to resolve any Dispute through informal negotiation within forty-five (45)
						days from the date the Notice of Dispute is received by us. If an agreement cannot be
						reached within forty-five (45) days of receipt of the Notice of Dispute, you or we may
						initiate an arbitration proceeding or small claims action as described below.
					</PostElementParagraph>
					<PostElementParagraph>
						You agree that the term &quot;Dispute&quot; in these Terms of Use will have the broadest
						meaning possible. It applies to any dispute, action, or other controversy between you
						and us relating to the Website, the Services, the Terms of Use (or any breach thereof) -
						whether in contract, warranty, tort, laws or regulation. The term also applies to any
						dispute over the validity, enforceability, or scope of this arbitration provision.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.arbitration}>{t("h3_6_2")}</PostElementTitleH3>
					<PostElementParagraph>
						If your Dispute is not resolved to your satisfaction within 45 days from when we
						received your Notice of Dispute, you and we agree to arbitrate all Disputes between us.
						The arbitration will take place in the applicable federal or state court in Delaware and
						be subject to the laws of the State of Delaware. You agree that arbitration will be your
						sole and exclusive remedy to resolve any dispute with us. The Federal Arbitration Act
						applies to this Agreement.
					</PostElementParagraph>
					<PostElementParagraph>
						By agreeing to arbitrate, you are giving up the right to litigate (or participate in as
						a party or class member) any and all Disputes in a court before a judge or jury .
						Instead, a neutral arbitrator will resolve all Disputes.
					</PostElementParagraph>
					<PostElementParagraph>
						To read more about arbitration, visit{" "}
						<PostElementLink href="https://www.adr.org/" target="_blank">
							https://www.adr.org/
						</PostElementLink>
						. The American Arbitration Association (&quot;AAA&quot;) will conduct any arbitration
						under its Consumer Arbitration Rules in effect at that time. You can review the Consumer
						Arbitration Rules here:{" "}
						<PostElementLink
							href="https://www.adr.org/sites/default/files/Consumer%20Rules.pdf"
							target="_blank"
						>
							https://www.adr.org/sites/default/files/Consumer%20Rules.pdf
						</PostElementLink>
						. You may begin an arbitration with us by completing a Demand for Arbitration form,
						available by clicking here:{" "}
						<PostElementLink
							href="https://www.adr.org/sites/default/files/Commercial%20Demand%20for%20Arbitration.pdf"
							target="_blank"
						>
							https://www.adr.org/sites/default/files/Commercial%20Demand%20for%20Arbitration.pdf
						</PostElementLink>
						. The completed form(s) should be returned directly to the AAA. The AAA will assign
						someone to manage your case. You will be contacted with instructions for continuing the
						arbitration process. Be sure to keep a copy of everything you send to the administrator
						of your arbitration or us. If, for any reason, the AAA is unavailable, the parties shall
						mutually select another arbitration forum which will be governed by the AAA Consumer
						Arbitration Rules. The AAA rules will determine whether the arbitration will take place
						through written submissions by you and us, by telephone, or in person.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.class_action_waiver}>
						{t("h3_6_3")}
					</PostElementTitleH3>
					<PostElementParagraph>
						Any proceedings to resolve or litigate any Dispute in any forum will be conducted solely
						on an individual basis. Class arbitrations, class actions, private attorney general
						actions, consolidation of your Dispute with other arbitrations, or any other proceeding
						in which either party acts or proposes to act in a representative capacity or as a
						private attorney general are not permitted and are waived by you, and an arbitrator will
						have no jurisdiction to hear such claims. If a court or arbitrator finds that the class
						action waiver in this section is unenforceable as to all or some parts of a Dispute,
						then the class action waiver will not apply to those parts. Instead, those parts will be
						severed and proceed in a court of law, with the remaining parts proceeding in
						arbitration. If any other provision of this Dispute resolution section is found to be
						illegal or unenforceable, that provision will be severed with the remainder of this
						section remaining in full force and effect.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.costs}>{t("h3_6_4")}</PostElementTitleH3>
					<PostElementParagraph>
						If you initiate an arbitration proceeding with the AAA, we will promptly reimburse you
						for any standard filing fee you may have been required to pay by the AAA, once you have
						notified us in writing and provided a copy of the arbitration demand. However, if we are
						the prevailing party in the arbitration, we will be entitled to seek and recover our
						attorneys&apos; fees and costs, subject to the arbitrator&apos;s determination and
						applicable law.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.location_data}>{t("h3_6_5")}</PostElementTitleH3>
					<PostElementParagraph>
						This site includes IP2Location LITE data available from{" "}
						<PostElementLink href="https://lite.ip2location.com/" target="_blank">
							https://lite.ip2location.com/
						</PostElementLink>
						.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.reCAPTCHA}>{t("h3_6_6")}</PostElementTitleH3>
					<PostElementParagraph>
						This site is protected by reCAPTCHA and the Google{" "}
						<PostElementLink href="https://policies.google.com/privacy" target="_blank">
							Privacy Policy
						</PostElementLink>{" "}
						and{" "}
						<PostElementLink href="https://policies.google.com/terms" target="_blank">
							Terms of Service
						</PostElementLink>{" "}
						apply.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.contact_details}>{t("h3_6_7")}</PostElementTitleH3>
					<PostElementParagraph>{t("p_6_7_1")}</PostElementParagraph>
					<PostElementParagraph>
						Furthermore, we comply with the Digital Millennium Copyright Act (&quot;DMCA&quot;). Any
						notices given pursuant to the DMCA shall be given to the GraphPad designated agent via{" "}
						<PostElementLink href="https://support.snapgene.com/hc/en-us/" target="_blank">
							support.snapgene.com
						</PostElementLink>{" "}
						or via registered US mail sent return receipt to: DMCA Compliance Agent, GSL Biotech,
						LLC, 225 Franklin Street. Fl. 26 BOSTON, MA 02110, USA.
					</PostElementParagraph>
					<PostElementParagraph>
						You may give notice to GSL Biotech at any time by letter sent by registered mail with
						return receipt to: GSL Biotech, LLC, 225 Franklin Street. Fl. 26 BOSTON, MA 02110, USA.
					</PostElementParagraph>
					<PostElementTitleH3 id={titleAnchor.entire_agreement}>{t("h3_6_8")}</PostElementTitleH3>
					<PostElementParagraph>{t("p_6_8_1")}</PostElementParagraph>
				</GuidePageTemplateMain>
			</GuidePageTemplate>
		</SiteLayout>
	);
};

export default pipe(LegalDisclaimers, WithLocale({ resources }));
